import { useCallback, useState } from 'react';

/**
 * State backed by localStorage under the design's `whoopws:` namespace, so the
 * workspace remembers choices (decisions, builder inputs, settings, experiments)
 * across reloads exactly as the source mockup did. Reads/writes are guarded so
 * private-mode or quota failures degrade to in-memory state rather than throwing.
 */
const PREFIX = 'whoopws:';

export function usePersistedState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(PREFIX + key);
      return raw == null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(PREFIX + key, JSON.stringify(next));
      } catch {
        // Ignore write failures (private mode / quota) — keep the in-memory value.
      }
    },
    [key],
  );

  return [value, set];
}
